import '../operations.css';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit, Trash2, Globe2, CheckCircle2, XCircle, RefreshCw, Search, Send, Sparkles } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Table } from '../../components/ui/Table';
import { Modal } from '../../components/ui/Modal';
import { sendingDomainsApi } from '../../api/sendingDomains';
import type { AssessmentMaterial, AssessmentSuggestion, AssessmentSuggestions, DomainScanResult, MailCheck, SendingDomainRequest } from '../../api/sendingDomains';
import { groupsApi } from '../../api/groups';
import { pagesApi } from '../../api/pages';
import { smtpApi } from '../../api/smtp';
import { templatesApi } from '../../api/templates';
import type { Group, SendingDomain } from '../../types';
import type { SendingProfile } from '../../api/smtp';

const emptyForm: SendingDomainRequest = { domain: '', dkim_selector: 'default', comment: '' };

function toFormState(domain: SendingDomain | null): SendingDomainRequest {
  if (!domain) return emptyForm;
  return { domain: domain.domain, dkim_selector: domain.dkim_selector || 'default', comment: domain.comment };
}

const scanStatusClass: Record<MailCheck['status'], string> = {
  pass: 'text-green-700 bg-green-50',
  missing: 'text-amber-800 bg-amber-50',
  invalid: 'text-red-700 bg-red-50',
  error: 'text-red-700 bg-red-50',
};

function scanErrorMessage(error: unknown): string {
  if (typeof error === 'object' && error && 'response' in error) {
    const message = (error as { response?: { data?: { message?: string } } }).response?.data?.message;
    if (message) return message;
  }
  return 'Domain scan failed. Try again.';
}

function RecordCard({ title, check }: { title: string; check: MailCheck }) {
  return (
    <div className="rounded-lg border border-gray-200 p-4 space-y-2">
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-medium text-gray-900">{title}</h3>
        <span className={`text-xs font-medium px-2 py-1 rounded-full capitalize ${scanStatusClass[check.status]}`}>{check.status}</span>
      </div>
      <p className="text-sm text-gray-600">{check.detail}</p>
      <p className="text-xs text-gray-400 break-all">{check.name}</p>
      {check.records.length > 0 ? check.records.map((record) => (
        <pre key={record} className="text-xs bg-gray-50 rounded p-2 whitespace-pre-wrap break-all">{record}</pre>
      )) : <p className="text-xs text-gray-400">No published record</p>}
    </div>
  );
}

function StatusBadge({ valid, label }: { valid: boolean; label: string }) {
  return valid
    ? <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 px-2 py-1 rounded-full"><CheckCircle2 className="w-3 h-3" /> {label}</span>
    : <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-50 px-2 py-1 rounded-full"><XCircle className="w-3 h-3" /> {label}</span>;
}

const draftPreviewPolicy = `<meta http-equiv="Content-Security-Policy" content="default-src 'none'; script-src 'none'; style-src 'unsafe-inline' https: http:; img-src data: blob: https: http:; font-src data: https: http:; media-src data: https: http:; form-action 'none'; base-uri 'none';">`;

function AssessmentDraftDialog({
  domain,
  suggestion,
  kind,
  onClose,
}: {
  domain: string;
  suggestion: AssessmentSuggestion;
  kind: 'email' | 'landing';
  onClose: () => void;
}) {
  const [material, setMaterial] = useState<AssessmentMaterial | null>(null);
  const [error, setError] = useState('');
  const [pending, setPending] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedHref, setSavedHref] = useState('');

  const generate = async () => {
    setPending(true);
    setError('');
    setMaterial(null);
    setSavedHref('');
    try {
      const res = await sendingDomainsApi.material({
        kind,
        domain,
        title: suggestion.title,
        reason: suggestion.reason,
        practice: suggestion.practice,
      });
      setMaterial(res.data);
    } catch (generateError) {
      setError(scanErrorMessage(generateError));
    } finally {
      setPending(false);
    }
  };

  useEffect(() => {
    void generate();
  }, [domain, kind, suggestion.title]);

  const save = async () => {
    if (!material) return;
    setSaving(true);
    setError('');
    try {
      if (kind === 'email') {
        await templatesApi.create({ name: material.name, subject: material.subject || suggestion.title, html: material.html, text: material.text });
        setSavedHref('/templates');
      } else {
        await pagesApi.create({ name: material.name, html: material.html });
        setSavedHref('/pages');
      }
    } catch (saveError) {
      setError(scanErrorMessage(saveError));
    } finally {
      setSaving(false);
    }
  };

  const title = kind === 'email' ? 'Make email' : 'Make landing page';
  return (
    <Modal isOpen onClose={onClose} title={title} size="xl">
      <div className="space-y-4">
        <p className="text-sm text-gray-500">{suggestion.title}</p>
        {pending && <p className="text-sm text-gray-500">Writing this with AI. A full draft can take a minute.</p>}
        {error && (
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-sm text-red-600" role="alert">{error}</p>
            {error.includes('Settings') && <a className="text-sm text-indigo-700 underline" href="/settings">Settings</a>}
            <Button type="button" variant="secondary" onClick={() => void generate()}>Try again</Button>
          </div>
        )}
        {material && (
          <>
            {kind === 'email' && <p className="text-sm text-gray-700"><span className="font-medium">Subject:</span> {material.subject}</p>}
            <iframe
              title={kind === 'email' ? 'Email preview' : 'Landing page preview'}
              sandbox=""
              className="h-80 w-full rounded-lg border border-gray-200 bg-white"
              srcDoc={`${draftPreviewPolicy}${material.preview_html || material.html}`}
            />
            {savedHref ? (
              <p className="text-sm text-gray-600">Saved. Open it in <a className="text-indigo-700 underline" href={savedHref}>{kind === 'email' ? 'Email templates' : 'Landing pages'}</a>.</p>
            ) : (
              <div className="flex justify-end">
                <Button type="button" onClick={() => void save()} loading={saving}><Sparkles className="w-4 h-4 mr-2" />Save {kind === 'email' ? 'email' : 'landing page'}</Button>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

function SpoofExerciseDialog({ domain, onClose }: { domain: string; onClose: () => void }) {
  const [groups, setGroups] = useState<Group[]>([]);
  const [profiles, setProfiles] = useState<SendingProfile[]>([]);
  const [groupId, setGroupId] = useState('');
  const [smtpId, setSmtpId] = useState('');
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);
  const [campaignId, setCampaignId] = useState<number | null>(null);
  const [loadingChoices, setLoadingChoices] = useState(true);

  useEffect(() => {
    let active = true;
    void Promise.all([groupsApi.getAll(), smtpApi.getAll()])
      .then(([groupResponse, profileResponse]) => {
        if (!active) return;
        setGroups(groupResponse.data ?? []);
        setProfiles(profileResponse.data ?? []);
      })
      .catch((loadError) => {
        if (active) setError(scanErrorMessage(loadError));
      })
      .finally(() => {
        if (active) setLoadingChoices(false);
      });
    return () => {
      active = false;
    };
  }, [domain]);

  const selectedGroup = groups.find((group) => String(group.id) === groupId);
  const send = async () => {
    setPending(true);
    setError('');
    try {
      const res = await sendingDomainsApi.spoofExercise({
        domain,
        group_id: Number(groupId),
        smtp_id: Number(smtpId),
      });
      setCampaignId(res.data.id);
    } catch (sendError) {
      setError(scanErrorMessage(sendError));
    } finally {
      setPending(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Send spoofing exercise" size="lg">
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          The visible sender will be IT Support &lt;it-support@{domain}&gt;. The message goes through the sending profile you choose, to everyone in the selected group.
        </p>
        {loadingChoices && <p className="text-sm text-gray-500">Loading groups and sending profiles.</p>}
        {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
        {campaignId ? (
          <p className="text-sm text-gray-600">
            The exercise is sending. Open <a className="text-indigo-700 underline" href={`/campaigns/${campaignId}`}>the campaign</a>.
          </p>
        ) : (
          <>
            <div>
              <label className="label" htmlFor="spoof-group">Group</label>
              <select id="spoof-group" className="input" value={groupId} onChange={(event) => setGroupId(event.target.value)}>
                <option value="">Choose a group</option>
                {groups.map((group) => (
                  <option key={group.id} value={group.id}>{group.name} ({group.targets?.length ?? 0})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label" htmlFor="spoof-profile">Sending profile</label>
              <select id="spoof-profile" className="input" value={smtpId} onChange={(event) => setSmtpId(event.target.value)}>
                <option value="">Choose a sending profile</option>
                {profiles.map((profile) => (
                  <option key={profile.id} value={profile.id}>{profile.name} ({profile.from_address})</option>
                ))}
              </select>
            </div>
            {selectedGroup && (
              <p className="text-sm text-gray-500">This sends the training message to {selectedGroup.targets?.length ?? 0} people in {selectedGroup.name}.</p>
            )}
            <div className="flex justify-end gap-3">
              <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
              <Button type="button" onClick={() => void send()} loading={pending} disabled={!groupId || !smtpId}>
                <Send className="w-4 h-4 mr-2" />Send to group
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

export function SendingDomainList() {
  const queryClient = useQueryClient();
  const [formModal, setFormModal] = useState<{ open: boolean; domain: SendingDomain | null }>({ open: false, domain: null });
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; domain: SendingDomain | null }>({ open: false, domain: null });
  const [form, setForm] = useState<SendingDomainRequest>(emptyForm);
  const [scanDomain, setScanDomain] = useState('');
  const [scanSelectors, setScanSelectors] = useState('');
  const [scanResult, setScanResult] = useState<DomainScanResult | null>(null);
  const [scanError, setScanError] = useState('');
  const [scanning, setScanning] = useState(false);
  const [suggestions, setSuggestions] = useState<AssessmentSuggestions | null>(null);
  const [suggestionsError, setSuggestionsError] = useState('');
  const [suggesting, setSuggesting] = useState(false);
  const [draft, setDraft] = useState<{ kind: 'email' | 'landing'; suggestion: AssessmentSuggestion } | null>(null);
  const [spoofOpen, setSpoofOpen] = useState(false);

  const { data: domains, isLoading } = useQuery({
    queryKey: ['sendingDomains'],
    queryFn: async () => {
      const res = await sendingDomainsApi.getAll();
      return res.data;
    },
  });

  const saveMutation = useMutation({
    mutationFn: (payload: SendingDomainRequest & { id?: number }) =>
      payload.id ? sendingDomainsApi.update(payload.id, payload) : sendingDomainsApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sendingDomains'] });
      setFormModal({ open: false, domain: null });
      setForm(emptyForm);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => sendingDomainsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sendingDomains'] });
      setDeleteModal({ open: false, domain: null });
    },
  });

  const verifyMutation = useMutation({
    mutationFn: (id: number) => sendingDomainsApi.verify(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sendingDomains'] }),
  });

  const openCreate = () => { setForm(emptyForm); setFormModal({ open: true, domain: null }); };
  const openEdit = (domain: SendingDomain) => { setForm(toFormState(domain)); setFormModal({ open: true, domain }); };

  const loadSuggestions = async (result: DomainScanResult) => {
    setSuggesting(true);
    setSuggestions(null);
    setSuggestionsError('');
    try {
      const res = await sendingDomainsApi.suggest(result);
      setSuggestions(res.data);
    } catch (error) {
      setSuggestionsError(scanErrorMessage(error));
    } finally {
      setSuggesting(false);
    }
  };

  const runScan = async (domainValue = scanDomain, selectorValue = scanSelectors) => {
    setScanning(true);
    setScanError('');
    setSuggestions(null);
    setSuggestionsError('');
    setDraft(null);
    setSpoofOpen(false);
    const selectors = selectorValue.split(',').map((selector) => selector.trim()).filter(Boolean);
    try {
      const res = await sendingDomainsApi.scan(domainValue, selectors);
      setScanResult(res.data);
      setScanDomain(res.data.domain);
      void loadSuggestions(res.data);
    } catch (error) {
      setScanResult(null);
      setScanError(scanErrorMessage(error));
    } finally {
      setScanning(false);
    }
  };

  const scanSavedDomain = (domain: SendingDomain) => {
    setScanDomain(domain.domain);
    setScanSelectors(domain.dkim_selector || '');
    void runScan(domain.domain, domain.dkim_selector || '');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload: SendingDomainRequest & { id?: number } = { ...form };
    if (formModal.domain) payload.id = formModal.domain.id;
    saveMutation.mutate(payload);
  };

  const columns = [
    {
      key: 'domain',
      header: 'Domain',
      render: (d: SendingDomain) => (
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-50 rounded-lg"><Globe2 className="w-4 h-4 text-indigo-600" /></div>
          <div>
            <p className="font-medium">{d.domain}</p>
            {d.comment && <p className="text-xs text-gray-500">{d.comment}</p>}
          </div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (d: SendingDomain) => (
        <div className="flex flex-wrap gap-2">
          {d.is_verified ? <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 px-2 py-1 rounded-full"><CheckCircle2 className="w-3 h-3" /> Verified</span> : <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 px-2 py-1 rounded-full"><XCircle className="w-3 h-3" /> Unverified</span>}
          <StatusBadge valid={d.spf_valid} label="SPF" />
          <StatusBadge valid={d.dkim_valid} label="DKIM" />
          <StatusBadge valid={d.dmarc_valid} label="DMARC" />
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (d: SendingDomain) => (
        <div className="flex items-center gap-2 justify-end">
          <button type="button" onClick={() => scanSavedDomain(d)} className="p-2 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors" title="Scan DNS" aria-label={`Scan ${d.domain}`}><Search className="w-4 h-4" /></button>
          <button type="button" onClick={() => verifyMutation.mutate(d.id)} className="p-2 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors" title="Verify DNS" aria-label="Verify DNS"><RefreshCw className={`w-4 h-4 ${verifyMutation.isPending && verifyMutation.variables === d.id ? 'animate-spin' : ''}`} /></button>
          <button type="button" onClick={() => openEdit(d)} className="p-2 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors" title="Edit" aria-label="Edit"><Edit className="w-4 h-4" /></button>
          <button type="button" onClick={() => setDeleteModal({ open: true, domain: d })} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
        </div>
      ),
    },
  ];

  return (
    <div className="operations-page space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="operations-eyebrow">Simulation infrastructure</p>
          <h1 className="text-2xl font-bold text-gray-900">Sending Domains</h1>
          <p className="text-gray-500 mt-1">Scan SPF, DKIM, DMARC, and MX for any domain, then save the ones you send from</p>
        </div>
        <Button onClick={openCreate}><Plus className="w-4 h-4 mr-2" />New Domain</Button>
      </div>
      <Card>
        <form className="space-y-4" onSubmit={(event) => { event.preventDefault(); void runScan(); }}>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Domain scanner</h2>
            <p className="text-sm text-gray-500 mt-1">Check the published mail records for any public domain. Leave selectors blank to try the common DKIM names.</p>
          </div>
          <div className="grid gap-4 md:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_auto] md:items-end">
            <div>
              <label className="label" htmlFor="scan-domain">Domain</label>
              <input id="scan-domain" type="text" className="input" value={scanDomain} onChange={(event) => setScanDomain(event.target.value)} placeholder="example.com or name@example.com" required />
            </div>
            <div>
              <label className="label" htmlFor="scan-selectors">DKIM selectors</label>
              <input id="scan-selectors" type="text" className="input" value={scanSelectors} onChange={(event) => setScanSelectors(event.target.value)} placeholder="google, selector1" />
            </div>
            <Button type="submit" loading={scanning}><Search className="w-4 h-4 mr-2" />Scan domain</Button>
          </div>
          {scanError && <p className="text-sm text-red-600" role="alert">{scanError}</p>}
          {scanResult && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-gray-600">Results for <span className="font-medium text-gray-900">{scanResult.domain}</span></p>
                {scanResult.impersonation?.allowed ? (
                  <Button type="button" onClick={() => setSpoofOpen(true)}><Send className="w-4 h-4 mr-2" />Send spoofing exercise</Button>
                ) : scanResult.impersonation ? (
                  <p className="text-sm text-gray-500">{scanResult.impersonation.reason}</p>
                ) : null}
              </div>
              <div className="grid gap-3 lg:grid-cols-2">
                <RecordCard title="SPF" check={scanResult.spf} />
                <RecordCard title="DMARC" check={scanResult.dmarc} />
                <RecordCard title="MX" check={scanResult.mx} />
                {scanResult.dkim.map((check) => <RecordCard key={check.name} title="DKIM" check={check} />)}
              </div>
              <div className="rounded-lg border border-gray-200 p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-indigo-600" />
                  <h3 className="font-medium text-gray-900">AI suggestions</h3>
                </div>
                {suggesting && <p className="text-sm text-gray-500">Choosing assessments for {scanResult.domain}. This can take a minute.</p>}
                {suggestionsError && (
                  <div className="flex flex-wrap items-center gap-3">
                    <p className="text-sm text-red-600" role="alert">{suggestionsError}</p>
                    {suggestionsError.includes('Settings') && <a className="text-sm text-indigo-700 underline" href="/settings">Settings</a>}
                    <Button type="button" variant="secondary" onClick={() => void loadSuggestions(scanResult)}>Try again</Button>
                  </div>
                )}
                {suggestions && (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-600">{suggestions.summary}</p>
                    <div className="grid gap-3 lg:grid-cols-2">
                      {suggestions.suggestions.map((suggestion) => (
                        <div key={suggestion.title} className="rounded-lg border border-gray-200 p-3 space-y-2">
                          <p className="font-medium text-gray-900">{suggestion.title}</p>
                          <p className="text-sm text-gray-600">{suggestion.reason}</p>
                          <p className="text-sm text-gray-500">{suggestion.practice}</p>
                          <div className="flex flex-wrap gap-2 pt-1">
                            <Button type="button" size="sm" variant="secondary" onClick={() => setDraft({ kind: 'email', suggestion })}><Sparkles className="w-4 h-4 mr-2" />Make email</Button>
                            <Button type="button" size="sm" variant="secondary" onClick={() => setDraft({ kind: 'landing', suggestion })}><Sparkles className="w-4 h-4 mr-2" />Make landing page</Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </form>
      </Card>

      {spoofOpen && scanResult?.impersonation?.allowed && (
        <SpoofExerciseDialog domain={scanResult.domain} onClose={() => setSpoofOpen(false)} />
      )}

      {draft && scanResult && (
        <AssessmentDraftDialog
          key={`${draft.kind}:${draft.suggestion.title}`}
          domain={scanResult.domain}
          suggestion={draft.suggestion}
          kind={draft.kind}
          onClose={() => setDraft(null)}
        />
      )}

      <Card>
        {isLoading ? (
          <div className="animate-pulse space-y-4">{[...Array(5)].map((_, i) => <div key={i} className="h-12 bg-gray-100 rounded" />)}</div>
        ) : (
          <Table columns={columns} data={domains ?? []} keyExtractor={(d) => d.id} emptyMessage="No sending domains configured." />
        )}
      </Card>

      <Modal isOpen={formModal.open} onClose={() => setFormModal({ open: false, domain: null })} title={formModal.domain ? 'Edit Sending Domain' : 'New Sending Domain'} size="lg">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label">Domain</label>
            <input type="text" className="input" value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value })} placeholder="training.example.com" required />
          </div>
          <div>
            <label className="label">DKIM selector</label>
            <input type="text" className="input" value={form.dkim_selector} onChange={(e) => setForm({ ...form, dkim_selector: e.target.value })} />
          </div>
          <div>
            <label className="label">Comment</label>
            <input type="text" className="input" value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="secondary" onClick={() => setFormModal({ open: false, domain: null })}>Cancel</Button>
            <Button type="submit" loading={saveMutation.isPending}>Save</Button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={deleteModal.open} onClose={() => setDeleteModal({ open: false, domain: null })} title="Delete sending domain" size="sm">
        <p>Delete <strong>{deleteModal.domain?.domain}</strong>?</p>
        <div className="flex justify-end gap-3 mt-4">
          <Button type="button" variant="secondary" onClick={() => setDeleteModal({ open: false, domain: null })}>Cancel</Button>
          <Button type="button" variant="danger" onClick={() => deleteModal.domain && deleteMutation.mutate(deleteModal.domain.id)} loading={deleteMutation.isPending}>Delete</Button>
        </div>
      </Modal>
    </div>
  );
}
